import React, { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  CheckBox,
  Title,
  DateRange,
  SelectForm,
  Row,
  AutoCompleteInput,
  Grid,
  Input
} from 'components'
import { MagnifyingGlassIcon } from 'components/Icons'
import {
  useDebounce,
  useLoading,
  useModal,
  useCheckListWithPagination,
  id
} from 'hooks'
import { useForm, useFormContext, UseFormReturn } from 'react-hook-form'
import {
  DjurServices,
  IGetDocumentsParams,
  IDocsItems,
  IMatriculaEmailSearchResponse,
  IResponseConsultAnalyzeDjur
} from 'services/NGT/DjurServices'
import { SPACING, OLDYUP as YUP, FUNCTIONS, FORMAT } from 'utils'
import { createArrayCopy, filterObject } from 'utils/functions'
import * as Yup from 'yup'

import { useDjurService } from '../../../../context'
import { IDocTypes } from '../../../../DJURService'
import { TableDocuments, TableDjurDocuments } from './components'

export interface IDocumentsFields {
  email: string
  matricula: string
  enviarEmail: boolean
}

// Campos no contexto global (onSubmit)
export const documentsSchema = Yup.object().shape({
  email: YUP.requiredWhen('enviarEmail').string,
  enviarEmail: Yup.bool(),
  matricula: YUP.requiredWhen('enviarEmail').string
})

// Apenas pra pesquisa e upload de Documentos
const thisSchema = Yup.object()
  .shape({
    IdTipoPendencia: YUP.required.number.test(
      'tipo-pendencia-todos',
      `Não pode ser "Todos"`,
      (value) => value !== 0.5
    ),
    // eslint-disable-next-line sort-keys
    cdContrato: YUP.required.string
  })
  .concat(
    YUP.dateRange('DtCompetenciaInicio', 'DtCompetenciaFim').endNotRequired
  )

interface IDocuments {
  documentTypes: IDocTypes
  isConsult: boolean
  triggerResetFields: boolean
  cleanState: boolean
  selectedDjur: IResponseConsultAnalyzeDjur
}

const Documents: React.FC<IDocuments> = ({
  documentTypes,
  isConsult,
  triggerResetFields,
  cleanState,
  selectedDjur,
}) => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { state, dispatch } = useDjurService()

  const [isSendEmail, setIsSendEmail] = useState<boolean>(false)
  const [documentosPage, setDocumentosPage] = useState<number>(1)
  const [documentosRowsPerPage, setDocumentosRowsPerPage] = useState<number>(15)
  const [documentosPageCount, setDocumentosPageCount] = useState<number>(0)
  const {
    data: documentos,
    setData: setDocumentos,
    selectedItems: selectedDocuments,
    handleClearSelectedItems,
    handleSelectItem,
    handleToWaive,
    handleCheckAll,
    isAllChecked
  } = useCheckListWithPagination<IDocsItems>({
    keyName: 'movDocumentoId',
    onSelectItem: (selectedDocuments) =>
      dispatch({
        payload: selectedDocuments,
        type: 'handleChangeSelectedDocuments'
      })
  })

  const [documentosParaDjur, setDocumentosParaDjur] = useState<IDocsItems[]>([])
  const [documentosParaCreateAndRemove, setDocumentoParaCreateAndRemove] =
    useState<IDocsItems[]>([])
  const [autocompleteOptions, setAutocompleteOptions] = useState<
    IMatriculaEmailSearchResponse[]
  >([])
  const [autoCompleteLoading, setAutoCompleteLoading] = useState<{
    matricula?: boolean
    email?: boolean
  }>(null)

  const form: UseFormReturn<any, object> & { errors?: any } = useFormContext()

  const DEFAULT_VALUES = {
    DtCompetenciaFim: form.getValues('DtCompetenciaFim_cache') || null,
    DtCompetenciaInicio: form.getValues('DtCompetenciaInicio_cache') || null,
    IdTipoPendencia: form.getValues('IdTipoPendencia_cache') || null,
    cdContrato: form.getValues('cdContrato_cache') || ''
  }

  const {
    control,
    watch,
    getValues,
    reset,
    formState: { errors }
  } = useForm<IGetDocumentsParams>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(thisSchema)
  })

  // Mantem os campos de pesquisa/upload de documento mesmo ao minimizar collapse
  useEffect(() => {
    const subscription = watch((fields, { name }) => {
      // Salva no contexto global como cache
      form.setValue(`${name}_cache`, fields[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const getTableDocumentosData = () => {
    setLoading('loading')

    const idTipoPendencia = getValues('IdTipoPendencia')
    const competenciaInicio = getValues('DtCompetenciaInicio')
    const competenciaFim = getValues('DtCompetenciaFim')

    DjurServices()
      .getInfoAtendimentoDjur({
        cdContrato: getValues('cdContrato'),
        cpfReclamante: state?.djurTarget?.cpfReclamante || FORMAT.unMask(form.getValues('cpfReclamante')),
        flDocumentos: true,
        idAtendimentoDjur: state?.djurTarget?.idAtendimentoDjur || 0,
        idTipoPendencia: form.getValues('tipoPendenciaId'),
        nuProcesso: state?.djurTarget?.processoReclamante || '',
        pageNumber: documentosPage,
        pageSize: documentosRowsPerPage,
        ...(idTipoPendencia && { idTipoPendencia }),
        ...(competenciaInicio && { competenciaInicio }),
        ...(competenciaFim && { competenciaFim })
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Erro ao obter dados do Atendimento Djur')
        }

        setDocumentos(response.data?.documento?.items)
        setDocumentosPageCount(response.data?.documento?.pageCount)
        setLoading('idle')
      })
      .catch(() => {
        showModal({
          hideButtonCancel: true,
          message: 'Erro ao obter dados do Atendimento Djur',
          txtButtonConfirm: 'Voltar',
          type: 'error'
        })
        setLoading('idle')
      })
  }

  useEffect(() => {
    const cpyArr: IDocsItems[] = createArrayCopy(documentosParaDjur)
    cpyArr.map((doc: IDocsItems) => {
      handleDeleteDjurDocument(doc)
    })

    setDocumentos(() => [])
  }, [cleanState])

  const getDocsForDjurTableData = () => {
    setLoading('loading')

    DjurServices()
      .getInfoAtendimentoDjur({
        cpfReclamante: state?.djurTarget?.cpfReclamante || FORMAT.unMask(form.getValues('cpfReclamante')),
        flDocumentosParaDjur: true,
        idAtendimentoDjur: state?.djurTarget?.idAtendimentoDjur || 0,
        nuProcesso: state?.djurTarget?.processoReclamante || ''
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Erro ao obter dados do Atendimento Djur')
        }

        if (response?.data?.documentosParaDjur) {
          setDocumentosParaDjur(response.data.documentosParaDjur)
        }
        setLoading('idle')
      })
  }

  const handleAddDocumentToDjur = () => {
    const selectedDocumentsForDjur = selectedDocuments.map((doc) => ({
      ...doc,
      idDocumentoParaDjur: 0,
      localId: id()
    }))
    const newDocsForDjur = [...documentosParaDjur, ...selectedDocumentsForDjur]

    if (
      FUNCTIONS.haveDuplicatedItems<IDocsItems>(
        newDocsForDjur,
        'movDocumentoId'
      )
    ) {
      showModal({
        hideButtonCancel: true,
        message:
          'Alguns dos itens selecionados já foram adicionados na tabela abaixo anteriormente, portanto serão ignorados',
        titleModal: 'Atenção!',
        txtButtonConfirm: 'OK',
        type: 'alert'
      })

      handleClearSelectedItems()
      return
    }

    setDocumentosParaDjur(
      FUNCTIONS.getUniqueItemsByProperties<IDocsItems>(newDocsForDjur, [
        'movDocumentoId'
      ])
    )
    setDocumentoParaCreateAndRemove((prevState) =>
      FUNCTIONS.getUniqueItemsByProperties(
        [...prevState, ...selectedDocumentsForDjur],
        ['movDocumentoId']
      )
    )
    handleClearSelectedItems()
  }

  useEffect(() => {
    dispatch({
      payload: documentosParaCreateAndRemove,
      type: 'handleChangeDocumentosParaDjur'
    })
  }, [documentosParaCreateAndRemove])

  const handleDeleteDjurDocument = (targetDoc: IDocsItems) => {
    if (targetDoc.localId) {
      setDocumentosParaDjur((prevState) =>
        prevState.filter(
          (doc) => !doc.localId || doc.localId !== targetDoc.localId
        )
      )
      setDocumentoParaCreateAndRemove((prevState) =>
        prevState.filter(
          (doc) => !doc.localId || doc.localId !== targetDoc.localId
        )
      )
      return
    }

    const removeDocItem = documentosParaDjur.find(
      (doc) => doc.movDocumentoId === targetDoc.movDocumentoId
    )

    setDocumentosParaDjur((prevState) =>
      prevState.filter((doc) => doc.movDocumentoId !== targetDoc.movDocumentoId)
    )
    setDocumentoParaCreateAndRemove((prevState) => [
      ...prevState,
      removeDocItem
    ])
  }

  const handleSearchEmailMatricula = ({ target: { name, value } }) => {
    setAutoCompleteLoading({ [name]: true })

    DjurServices()
      .searchMatriculaEmail({
        flMatricula: name.includes('matricula'),
        searchParam: value
      })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Houve um erro ao buscar os dados')
        }

        setAutocompleteOptions(response.data)
        setAutoCompleteLoading({ [name]: false })
      })
      .catch((error) => {
        setAutoCompleteLoading({ [name]: false })
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: 'error'
        })
      })
  }

  const handleCompleteFields = (fields: {
    txEmail?: string
    nmMatricula?: string
  }) => {
    const [fieldChangeName] = Object.keys(filterObject(fields, Boolean))

    const target = autocompleteOptions.find(
      (option) => option[fieldChangeName] === fields[fieldChangeName]
    )

    form.setValue('email', target?.txEmail || '')
    form.setValue('matricula', target?.nmMatricula || '')
  }

  const clearDocumentFields = () => {
    reset()
  }

  const handleCheckSendEmail = () => {
    if (!form.getValues('email')) {
      showModal({
        hideButtonCancel: true,
        message: 'Você deve selecionar um email para marcar está opção',
        titleModal: 'Atenção',
        type: 'alert'
      })
      setIsSendEmail(false)
      form.setValue('enviarEmail', false)
      return
    }

    form.setValue('enviarEmail', !isSendEmail)
    setIsSendEmail((prevState) => !prevState)
  }

  // Reseta os campos de pesquisa/upload
  useEffect(() => {
    if (triggerResetFields) {
      clearDocumentFields()
    }
  }, [triggerResetFields])

  useEffect(() => {
    getDocsForDjurTableData()
  }, [])

  useEffect(() => {
    const isFirstMount = (state.refreshDocumentosParadjur === null) as boolean

    if (!isFirstMount) {
      getDocsForDjurTableData()
    }
  }, [state.refreshDocumentosParadjur])

  useEffect(() => {
    getTableDocumentosData()
  }, [documentosPage, documentosRowsPerPage])

  useEffect(() => {
    const isFirstMount = (state.refreshDocumentos === null) as boolean

    if (!isFirstMount) {
      setDocumentosPage(1)
      getTableDocumentosData()
    }
  }, [state.refreshDocumentos])

  const clearFieldsAutoComplete = () => {
    form.setValue('email', '')
    form.setValue('matricula', '')
  }

  useEffect(() => {
    if (!form.getValues('email')) {
      setIsSendEmail(false)
      form.setValue('enviarEmail', false)
    }
  }, [form.watch('email')])

  return (
    <>
      <Row alignItems="flex-start">
        <Input
          control={control}
          errors={errors}
          formValue={'cdContrato'}
          label="Contrato"
          placeholder="Nº"
          marginRight={SPACING.spacing15}
          maxLength={100}
          disabled={isConsult}
        />
        <SelectForm
          control={control}
          errors={errors}
          formValue="IdTipoPendencia"
          label="Tipo"
          listOptions={[{ label: 'Todos', value: 0 }, ...documentTypes]}
          labelMenu="label"
          valueMenu="value"
          placeholder="Placeholder"
          marginRight={SPACING.spacing15}
          width="150px"
          disabled={isConsult}
          required
        />
        <DateRange
          watch={watch}
          control={control}
          errors={errors}
          formValue={['DtCompetenciaInicio', 'DtCompetenciaFim']}
          label={['Período Competência', '']}
          width={['150px', '150px']}
          marginRight={[null, SPACING.spacing15]}
          type={'monthAndYear'}
          disabled={isConsult}
        />
        {!isConsult && (
          <MagnifyingGlassIcon
            onClick={getTableDocumentosData}
            marginRight={SPACING.spacing50}
            marginTop={'auto'}
            marginBottom={'auto'}
          />
        )}
      </Row>
      <TableDocuments
        tableData={selectedDjur?.documento?.items || documentos}
        paginationControl={[documentosPage, setDocumentosPage]}
        rowsPerPageControl={[documentosRowsPerPage, setDocumentosRowsPerPage]}
        pageCount={documentosPageCount}
        handleSelectItem={handleSelectItem}
        handleToWaive={handleToWaive}
        handleCheckAll={handleCheckAll}
        isAllChecked={isAllChecked}
        isConsult={isConsult}
      />
      {!isConsult && (
        <Row alignItems="center" justifyContent="end">
          <Button text="Adicionar" onClick={handleAddDocumentToDjur} />
        </Row>
      )}
      <Title fontsize="16px" margintop={SPACING.spacing10}>
        Documentos para DJUR
      </Title>
      <TableDjurDocuments
        tableData={documentosParaDjur}
        handleDeleteDjurDocument={handleDeleteDjurDocument}
        isConsult={isConsult}
      />
      <Row alignItems="center" marginTop={SPACING.spacing10}>
        <Grid width="100%" gridCols="200px 300px" gridGap={SPACING.spacing15}>
          <AutoCompleteInput
            control={form.control}
            errors={form.errors}
            options={autocompleteOptions || []}
            marginTop={SPACING.spacing5}
            onChangeText={useDebounce(handleSearchEmailMatricula, 500)}
            formValue={'matricula'}
            optionValue="nmMatricula"
            label="Matrícula"
            onChange={(selectedMatricula) => {
              if (!selectedMatricula) {
                clearFieldsAutoComplete()
                return
              }

              handleCompleteFields({ nmMatricula: selectedMatricula })
            }}
            valueName="nmMatricula"
            loading={autoCompleteLoading?.matricula}
            type="dynamic"
            disabled={isConsult}
          />
          <AutoCompleteInput
            control={form.control}
            errors={form.errors}
            options={autocompleteOptions || []}
            marginTop={SPACING.spacing5}
            onChangeText={useDebounce(handleSearchEmailMatricula, 500)}
            formValue={'email'}
            optionValue="txEmail"
            label="E-mail"
            onChange={(selectedEmail) => {
              if (!selectedEmail) {
                clearFieldsAutoComplete()
                return
              }

              handleCompleteFields({ txEmail: selectedEmail })
            }}
            valueName="txEmail"
            loading={autoCompleteLoading?.email}
            type="dynamic"
            disabled={isConsult}
          />
        </Grid>
      </Row>
      <Row alignItems="center" marginTop={SPACING.spacing10}>
        <CheckBox
          // control={form.control}
          formValue="enviarEmail"
          onChange={handleCheckSendEmail}
          name="enviarEmail"
          checked={isSendEmail}
          label="Enviar documentos para o E-mail responsável acima"
          disabled={isConsult}
        />
      </Row>
    </>
  )
}

export default Documents
